import React, {Component} from 'react';
import PageHeader from "../PageHeader";
import Menu from "../Menu";
import Preloader from "../Preloader";
import api from "../../api";
import './CreateRecord.scss';

type ServiceItem = {
    id: number,
    title: string,
    price?: number | null,
    length?: number | null,
    priceDisplay?: string,
    lengthDisplay?: string,
}

type ServiceGroupItem = {
    id: number,
    title: string,
    services: ServiceItem[],
}

type StaffItem = {
    id: number,
    name: string,
    isCurrent: boolean,
}

type CompanyItem = {
    id: number,
    title: string,
    address: string,
    city: string,
    ownStaffId: number | null,
    staff: StaffItem[],
    serviceGroups: ServiceGroupItem[],
}

type BootstrapResponse = {
    defaultCompanyId: number | null,
    defaultStaffId: number | null,
    companies: CompanyItem[],
}

export class CreateRecord extends Component<any, any> {
    constructor(props: any) {
        super(props);
        window.scrollTo(0, 0);

        this.state = {
            isBootLoading: true,
            isSubmitting: false,
            loadError: '',
            submitError: '',
            successMessage: '',
            bootstrap: null as BootstrapResponse | null,
            selectedCompanyId: 0,
            selectedStaffId: 0,
            selectedServiceIds: [] as number[],
            clientPhone: '',
            clientName: '',
            recordDate: '',
            recordTime: '',
            comment: '',
            createdBooking: null as any,
        };
    }

    componentDidMount() {
        this.loadBootstrap();
    }

    loadBootstrap = () => {
        this.setState({
            isBootLoading: true,
            loadError: '',
        });

        api({
            url: '/widget/staff/v2/profile/record/bootstrap',
            method: 'GET',
        }).then((result) => {
            const bootstrap = result.data as BootstrapResponse;
            const companies = Array.isArray(bootstrap?.companies) ? bootstrap.companies : [];
            const selectedCompanyId = bootstrap?.defaultCompanyId || (companies[0] ? companies[0].id : 0);
            const selectedCompany = companies.find((company) => company.id === selectedCompanyId);
            const selectedStaffId = this.getDefaultStaffId(selectedCompany, bootstrap?.defaultStaffId || 0);

            this.setState({
                isBootLoading: false,
                bootstrap,
                selectedCompanyId,
                selectedStaffId,
            });
        }).catch((error) => {
            const message = error?.response?.data?.message || 'Не удалось загрузить форму записи. Попробуйте ещё раз.';
            this.setState({
                isBootLoading: false,
                loadError: message,
            });
        });
    }

    getDefaultStaffId = (company: CompanyItem | undefined, preferredStaffId: number) => {
        if (!company) {
            return 0;
        }

        if (preferredStaffId > 0) {
            if (preferredStaffId === 1 && company.staff.length > 1) {
                return 1;
            }

            const preferred = company.staff.find((staff) => staff.id === preferredStaffId);
            if (preferred) {
                return preferred.id;
            }
        }

        if (company.ownStaffId) {
            return company.ownStaffId;
        }

        if (company.staff.length > 1) {
            return 1;
        }

        return company.staff[0] ? company.staff[0].id : 0;
    }

    getSelectedCompany = (): CompanyItem | undefined => {
        const bootstrap = this.state.bootstrap as BootstrapResponse | null;
        if (!bootstrap || !Array.isArray(bootstrap.companies)) {
            return undefined;
        }

        return bootstrap.companies.find((company) => company.id === Number(this.state.selectedCompanyId));
    }

    getServiceById = (company: CompanyItem | undefined, serviceId: number): ServiceItem | undefined => {
        if (!company) {
            return undefined;
        }

        for (let i = 0; i < company.serviceGroups.length; i++) {
            const group = company.serviceGroups[i];
            const service = group.services.find((item) => item.id === serviceId);
            if (service) {
                return service;
            }
        }

        return undefined;
    }

    handleCompanyChange = (event: any) => {
        const selectedCompanyId = Number(event.target.value) || 0;
        const bootstrap = this.state.bootstrap as BootstrapResponse | null;
        const company = bootstrap?.companies.find((item) => item.id === selectedCompanyId);
        const selectedStaffId = this.getDefaultStaffId(company, 0);

        this.setState({
            selectedCompanyId,
            selectedStaffId,
            selectedServiceIds: [],
            submitError: '',
            successMessage: '',
            createdBooking: null,
        });
    }

    handleStaffChange = (event: any) => {
        const selectedStaffId = Number(event.target.value) || 0;

        this.setState({
            selectedStaffId,
            submitError: '',
            successMessage: '',
            createdBooking: null,
        });
    }

    toggleService = (serviceId: number) => {
        const company = this.getSelectedCompany();
        const service = this.getServiceById(company, serviceId);
        if (!service) {
            return;
        }

        const selectedServiceIds = [...this.state.selectedServiceIds];
        const index = selectedServiceIds.indexOf(serviceId);
        if (index >= 0) {
            selectedServiceIds.splice(index, 1);
        } else {
            selectedServiceIds.push(serviceId);
        }

        this.setState({
            selectedServiceIds,
            submitError: '',
            successMessage: '',
            createdBooking: null,
        });
    }

    handleFieldChange = (fieldName: string) => (event: any) => {
        this.setState({
            [fieldName]: event.target.value,
            submitError: '',
            successMessage: '',
            createdBooking: null,
        });
    }

    normalizePhone = (phone: string) => {
        const digits = String(phone || '').replace(/\D+/g, '');
        if (digits.length === 11 && digits.charAt(0) === '8') {
            return '7' + digits.slice(1);
        }

        return digits;
    }

    getSelectedServicesSummary = () => {
        const company = this.getSelectedCompany();
        const selectedServiceIds = this.state.selectedServiceIds as number[];
        let totalLength = 0;
        const selectedServices: ServiceItem[] = [];

        selectedServiceIds.forEach((serviceId) => {
            const service = this.getServiceById(company, serviceId);
            if (!service) {
                return;
            }

            selectedServices.push(service);
            totalLength += Number(service.length || 0);
        });

        return {
            selectedServices,
            totalLength,
        };
    }

    handleSubmit = () => {
        const company = this.getSelectedCompany();
        const selectedCompanyId = Number(this.state.selectedCompanyId);
        const selectedStaffId = Number(this.state.selectedStaffId);
        const selectedServiceIds = this.state.selectedServiceIds as number[];
        const clientName = String(this.state.clientName || '').trim();
        const clientPhone = String(this.state.clientPhone || '').trim();
        const recordDate = String(this.state.recordDate || '').trim();
        const recordTime = String(this.state.recordTime || '').trim();
        const comment = String(this.state.comment || '').trim();

        if (!company || selectedCompanyId <= 0) {
            this.setState({submitError: 'Выберите филиал.'});
            return;
        }

        if (selectedStaffId <= 0) {
            this.setState({submitError: 'Выберите мастера.'});
            return;
        }

        if (!selectedServiceIds.length) {
            this.setState({submitError: 'Выберите хотя бы одну услугу.'});
            return;
        }

        if (!recordDate || !recordTime) {
            this.setState({submitError: 'Выберите дату и время записи.'});
            return;
        }

        if (!clientName) {
            this.setState({submitError: 'Укажите имя клиента.'});
            return;
        }

        if (this.normalizePhone(clientPhone).length !== 11) {
            this.setState({submitError: 'Введите корректный номер телефона клиента.'});
            return;
        }

        this.setState({
            isSubmitting: true,
            submitError: '',
            successMessage: '',
            createdBooking: null,
        });

        api({
            url: '/widget/staff/v2/profile/record/create',
            method: 'POST',
            data: {
                companyId: selectedCompanyId,
                booking: {
                    companyId: selectedCompanyId,
                    resourceId: selectedStaffId,
                    serviceIds: selectedServiceIds,
                    datetime: `${recordDate} ${recordTime}`,
                },
                client: {
                    phone: clientPhone,
                    name: clientName,
                },
                commentRecord: comment,
            },
        }).then((result) => {
            this.setState({
                isSubmitting: false,
                successMessage: result?.data?.message || 'Запись создана.',
                createdBooking: result?.data?.booking || null,
                clientName: '',
                clientPhone: '',
                comment: '',
            });
        }).catch((error) => {
            this.setState({
                isSubmitting: false,
                submitError: error?.response?.data?.message || 'Не удалось создать запись. Попробуйте ещё раз.',
            });
        });
    }

    renderServiceGroups() {
        const company = this.getSelectedCompany();
        if (!company) {
            return null;
        }

        if (!company.serviceGroups.length) {
            return (
                <div className='create-record-empty'>
                    Для этого филиала пока не найдено активных услуг.
                </div>
            );
        }

        return company.serviceGroups.map((group) => (
            <div className='create-record-services-group' key={group.id}>
                <div className='create-record-group-title'>{group.title}</div>
                <div className='create-record-services-list'>
                    {group.services.map((service) => {
                        const isChecked = this.state.selectedServiceIds.includes(service.id);

                        return (
                            <button
                                type='button'
                                key={service.id}
                                className={`create-record-service-item${isChecked ? ' is-active' : ''}`}
                                onClick={() => this.toggleService(service.id)}
                                aria-pressed={isChecked}
                            >
                                <span className='create-record-service-main'>
                                    <span className='create-record-service-title'>{service.title}</span>
                                    <span className='create-record-service-meta'>
                                        {service.lengthDisplay ? `${service.lengthDisplay} мин` : 'Длительность по салону'}
                                        {service.priceDisplay ? ` · ${service.priceDisplay} р` : ''}
                                    </span>
                                </span>
                                <span className='create-record-service-check'>
                                    {isChecked ? 'Выбрано' : 'Выбрать'}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        ));
    }

    renderSelectedServices() {
        const summary = this.getSelectedServicesSummary();

        if (!summary.selectedServices.length) {
            return (
                <div className='create-record-empty'>
                    Выберите услуги, которые клиент хочет получить.
                </div>
            );
        }

        return (
            <div>
                <div className='create-record-summary-row'>
                    <span>Выбрано услуг</span>
                    <strong>{summary.selectedServices.length}</strong>
                </div>
                <div className='create-record-summary-row'>
                    <span>Общая длительность</span>
                    <strong>{summary.totalLength || 0} мин</strong>
                </div>

                <div className='create-record-selected-services'>
                    {summary.selectedServices.map((service) => (
                        <div className='create-record-selected-service' key={service.id}>
                            <span>{service.title}</span>
                            <button type='button' onClick={() => this.toggleService(service.id)} aria-label={'Убрать услугу ' + service.title}>
                                Убрать
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderSuccess() {
        const createdBooking = this.state.createdBooking;
        if (!this.state.successMessage) {
            return null;
        }

        return (
            <div className='create-record-card create-record-success'>
                <div className='create-record-success-title'>Клиент записан</div>
                <div className='create-record-success-text'>{this.state.successMessage}</div>
                <div className='create-record-success-text'>
                    Дата и время: <strong>{createdBooking?.datetime || `${this.state.recordDate} ${this.state.recordTime}`}</strong>
                </div>
                <div className='create-record-success-actions'>
                    <button
                        type='button'
                        className='btn btn-outline-dark'
                        onClick={() => this.setState({createdBooking: null, successMessage: '', selectedServiceIds: [], recordDate: '', recordTime: ''})}
                    >
                        Новая запись
                    </button>
                    <a className='btn btn-dark' href='/records'>К моим записям</a>
                </div>
            </div>
        );
    }

    render() {
        if (this.state.isBootLoading) {
            return (<Preloader></Preloader>);
        }

        const company = this.getSelectedCompany();
        const canUseAnyMaster = company && company.staff.length > 1;

        return (
            <div className='main schedule create-record-page'>
                <div className='content mx-w-content position-relative'>
                    <PageHeader
                        pageTitle='Записать клиента'
                        backBtnUrl='/'
                    />

                    <div className='pt-header'></div>

                    <div className='create-record-body'>
                        {this.state.loadError ? (
                            <div className='create-record-card create-record-error'>
                                <div className='create-record-error-text'>{this.state.loadError}</div>
                                <button type='button' className='btn btn-dark mt-3' onClick={this.loadBootstrap}>
                                    Обновить
                                </button>
                            </div>
                        ) : null}

                        {!this.state.loadError && (!this.state.bootstrap || !this.state.bootstrap.companies.length) ? (
                            <div className='create-record-card create-record-empty'>
                                Для вашей учётной записи пока не найдено доступных филиалов.
                            </div>
                        ) : null}

                        {!this.state.loadError && this.state.bootstrap && this.state.bootstrap.companies.length && this.state.successMessage ? this.renderSuccess() : null}

                        {!this.state.loadError && this.state.bootstrap && this.state.bootstrap.companies.length && !this.state.successMessage ? (
                            <>
                                <div className='create-record-card'>
                                    <div className='create-record-section-title'>Параметры записи</div>

                                    <label className='create-record-label' htmlFor='create-record-company'>Филиал</label>
                                    <select
                                        id='create-record-company'
                                        className='form-select create-record-input'
                                        value={this.state.selectedCompanyId}
                                        onChange={this.handleCompanyChange}
                                    >
                                        <option value={0}>Выберите филиал</option>
                                        {this.state.bootstrap.companies.map((companyItem: CompanyItem) => (
                                            <option key={companyItem.id} value={companyItem.id}>
                                                {companyItem.city}, {companyItem.address}
                                            </option>
                                        ))}
                                    </select>

                                    <label className='create-record-label' htmlFor='create-record-staff'>Мастер</label>
                                    <select
                                        id='create-record-staff'
                                        className='form-select create-record-input'
                                        value={this.state.selectedStaffId}
                                        onChange={this.handleStaffChange}
                                    >
                                        <option value={0}>Выберите мастера</option>
                                        {canUseAnyMaster ? <option value={1}>Любой свободный мастер</option> : null}
                                        {company?.staff.map((staffItem) => (
                                            <option key={staffItem.id} value={staffItem.id}>
                                                {staffItem.name}{staffItem.isCurrent ? ' · мой профиль' : ''}
                                            </option>
                                        ))}
                                    </select>

                                    <div className='create-record-grid'>
                                        <div>
                                            <label className='create-record-label' htmlFor='create-record-date'>Дата</label>
                                            <input
                                                id='create-record-date'
                                                className='form-control create-record-input'
                                                type='date'
                                                value={this.state.recordDate}
                                                onChange={this.handleFieldChange('recordDate')}
                                            />
                                        </div>
                                        <div>
                                            <label className='create-record-label' htmlFor='create-record-time'>Время</label>
                                            <input
                                                id='create-record-time'
                                                className='form-control create-record-input'
                                                type='time'
                                                value={this.state.recordTime}
                                                onChange={this.handleFieldChange('recordTime')}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className='create-record-card'>
                                    <div className='create-record-section-title'>Клиент</div>

                                    <label className='create-record-label' htmlFor='create-record-name'>Имя клиента</label>
                                    <input
                                        id='create-record-name'
                                        type='text'
                                        className='form-control create-record-input'
                                        placeholder='Например, Анна'
                                        autoComplete='name'
                                        value={this.state.clientName}
                                        onChange={this.handleFieldChange('clientName')}
                                    />

                                    <label className='create-record-label' htmlFor='create-record-phone'>Телефон</label>
                                    <input
                                        id='create-record-phone'
                                        type='tel'
                                        className='form-control create-record-input'
                                        placeholder='+7 999 123-45-67'
                                        autoComplete='tel'
                                        value={this.state.clientPhone}
                                        onChange={this.handleFieldChange('clientPhone')}
                                    />

                                    <details className='create-record-comment'>
                                        <summary>Добавить комментарий <span>необязательно</span></summary>
                                        <label className='sr-only' htmlFor='create-record-comment'>Комментарий к записи</label>
                                        <textarea
                                            id='create-record-comment'
                                            className='form-control create-record-input create-record-textarea'
                                            placeholder='Пожелания клиента к записи'
                                            value={this.state.comment}
                                            onChange={this.handleFieldChange('comment')}
                                        />
                                    </details>
                                </div>

                                <div className='create-record-card'>
                                    <div className='create-record-section-title'>Услуги</div>
                                    {this.renderServiceGroups()}
                                </div>

                                <div className='create-record-card'>
                                    <div className='create-record-section-title'>Что попадёт в запись</div>
                                    {this.renderSelectedServices()}
                                </div>

                                <div className='create-record-submit-bar'>
                                    {this.state.submitError ? <div className='create-record-submit-error' role='alert'>{this.state.submitError}</div> : null}
                                    <button
                                        type='button'
                                        className='create-record-submit'
                                        disabled={this.state.isSubmitting}
                                        onClick={this.handleSubmit}
                                    >
                                        {this.state.isSubmitting ? 'Сохраняем запись...' : 'Записать клиента'}
                                    </button>
                                </div>
                            </>
                        ) : null}
                    </div>
                </div>
                <Menu></Menu>
            </div>
        );
    }
}

export default CreateRecord;
